<template>
  <DataTable
    class="shadow-sm"
    title="Products"
    :loading="loading"
    :rows="rows"
    :cols-map="{
      'Product Name': 'name',
      'Product Type': 'product_type_id',
      Regions: 'region_ids',
      'Lifecycle Stage': 'current_lifecycle_stage',
      'Lifecycle End Date': 'current_lifecycle_end_date',
    }"
    :nested-cols-map="{
      'Product Offering Name': 'name',
      'Measure Type': 'measure_method',
      'Measure Method': 'measure_type',
      'Has Sphere Content': 'has_sphere_content',
    }"
    nested-key="offerings"
    sortable
    selectable
    expandable
    searchable
    createable
    :updatable-inline="!loading"
    deletable
    :deletable-inline="!loading"
    paginated
    :count="count"
    :page="currentPage"
    :total="products?.total"
    :limit="products?.limit"
    :error-message="errorMessage"
    @create="$emit('create')"
    @delete="(ids: string[]) => $emit('deleteProducts', ids)"
    @update:page="(offset: number) => updateOffset(offset)"
    @update:page-limit="(limit: number) => emits('updatePageLimit', limit)"
    @search="(search: string) => $emit('search', search)"
    @update-inline="(row) => $emit('updateProduct', String(row.id))"
    @delete-inline="(row) => $emit('deleteProducts', [String(row.id)])"
  >
    <template #toolbar-actions>
      <ProductsInputBar
        :selected-filter-lifecycle="selectedLifecycle"
        @update-filter-lifecycle="(lifecycle: string) => $emit('updateLifecycle', lifecycle)"
      />
    </template>
    <template #customCells="{row, cols}">
      <template v-for="(col, _index) in cols" :key="_index">
        <td v-if="col === 'product_type_id'">
          <template v-if="productTypes !== null">
            {{ productTypes.find((pt) => pt.id === row[col as keyof Product])?.name }}
          </template>
          <template v-else>-</template>
        </td>
        <td v-else-if="col === 'region_ids'">
          <template v-if="getFlagCodes(row[col]).length > 0">
            <div v-for="code in getFlagCodes(row[col])" :key="code" class="d-inline">
              <span class="fi m-1" :class="code" />
            </div>
          </template>
          <template v-else>-</template>
        </td>
        <td v-else-if="col === 'current_lifecycle_stage'">
          <template v-if="getLifeCycleBadge(row[col])">
            <BootstrapBadge :variant="getLifeCycleBadge(row[col]) || 'primary'">{{
              row[col]
            }}</BootstrapBadge>
          </template>
          <template v-else>-</template>
        </td>
        <DataTableCell v-else :row="row" :col="col" :cols-values="cols" />
      </template>
    </template>
    <template #nestedRow="{nestedRows, nestedColsMap}">
      <DataTable
        class="z-1"
        :rows="nestedRows"
        :cols-map="nestedColsMap"
        nested
        selectable
        deletable
        :updatable-inline="loading ? false : true"
        :deletable-inline="loading ? false : true"
        @update-inline="(row) => $emit('updateProductOffering', String(row.id))"
        @delete="(ids: string[]) => $emit('deleteProductOfferings', ids)"
        @delete-inline="(row) => $emit('deleteProductOfferings', [String(row.id)])"
      />
    </template>
  </DataTable>
</template>
<script lang="ts" setup>
import {BootstrapThemeColour} from '@/components/shared/bootstrap';
import {Product} from '@/stores/userManagement/userManagement';

interface Props {
  products: PaginatedProduct | null;
  loading: boolean;
  selectedSku: string | undefined;
  productTypes: ProductType[] | null;
  errorMessage: string;
  selectedLifecycle?: string;
}

const props = withDefaults(defineProps<Props>(), {
  products: null,
  loading: false,
  selectedSku: undefined,
  productTypes: null,
  errorMessage: '',
  selectedLifecycle: undefined,
});

const emits = defineEmits<{
  search: [search: string];
  create: [];
  updateProduct: [id: string];
  deleteProducts: [ids: string[]];
  updateProductOffering: [id: string];
  deleteProductOfferings: [ids: string[]];
  updateOffset: [offset: number];
  updatePageLimit: [limit: number];
  updateLifecycle: [value: string];
}>();

const rows = computed(() => {
  let items: Product[] = [];
  // format `current_lifecycle_end_date` to not show null
  if (props.products?.items) {
    items = [...props.products.items];
    items.forEach(
      (element) => (element.current_lifecycle_end_date = element.current_lifecycle_end_date || ''),
    );
  }
  return items ?? [];
});

const calculatedPage = computed(() =>
  props.products !== null ? Math.ceil((props.products.offset - 1) / props.products.limit) + 1 : 1,
);
const currentPage = ref(calculatedPage.value);

const count = computed(() =>
  props.products !== null ? Math.ceil(props.products.total / props.products.limit) : 1,
);

const updateOffset = (offset: number) => {
  emits('updateOffset', offset);
};

const selectedSku = computed(() => props.selectedSku);
watch(selectedSku, (n, o) => {
  // If the sku changes, reset the stepper
  if (o !== undefined && n !== o) currentPage.value = 1;
});

const flagMap = new Map([
  ['IE', 'fi-ie'],
  ['UK', 'fi-gb'],
  ['NL', 'fi-nl'],
]);
const getFlagCodes = (ids: string[]) => {
  const codes: string[] = [];
  for (const id of ids) {
    const code = flagMap.get(id);
    if (code) codes.push(code);
  }
  return codes;
};

const lifeCycleMap = new Map<string, BootstrapThemeColour>([
  ['In Development', 'secondary'],
  ['Production', 'success'],
  ['End of Sale', 'warning'],
  ['End of Support', 'danger'],
  ['End of Life', 'info'],
]);
const getLifeCycleBadge = (value: string) => {
  return lifeCycleMap.has(value) ? lifeCycleMap.get(value) : undefined;
};
</script>
